<template>
    <div class="wc-page">
        <Top name="提现配置" back email></Top>
        <div class="wc-tip">提前保存收款配置，提现时一键选用，无需每次重复填写。</div>

        <!-- 配置列表 -->
        <div class="wc-list" v-if="list.length">
            <div class="wc-card" v-for="item in list" :key="item.id">
                <div class="wc-card-head">
                    <span class="wc-tag" :class="item.pay_type">{{ ['waffo', 'onerway'].includes(item.pay_type) ? ('法币 ' + item.currency) : (item.currency + ' ' + item.network) }}</span>
                    <span class="wc-label">{{ item.label || defaultLabel(item) }}</span>
                </div>
                <div class="wc-card-body">
                    <template v-if="['waffo', 'onerway'].includes(item.pay_type)">
                        <p><span>国家/地区</span>{{ item.country_name }}（{{ item.payee_country }}）</p>
                        <p><span>银行</span>{{ item.bank_name || '—' }}（代码 {{ item.bank_code }}<template v-if="item.extra && item.extra.bankBranch"> / 支行 {{ item.extra.bankBranch }}</template>）</p>
                        <p><span>收款账号</span>{{ mask(item.payout_account_no) }}</p>
                        <p><span>收款人</span>{{ item.payee_name }}</p>
                    </template>
                    <template v-else>
                        <p><span>币种/网络</span>{{ item.currency }} / {{ item.network }}</p>
                        <p><span>钱包地址</span>{{ mask(item.payout_account_no || item.wallet_address) }}</p>
                    </template>
                </div>
                <div class="wc-card-actions">
                    <van-button size="small" plain round color="#ff4e00" @click="openEditor(item)">编辑</van-button>
                    <van-button size="small" plain round type="danger" @click="delConfig(item)">删除</van-button>
                </div>
            </div>
        </div>
        <div class="wc-empty" v-else>
            <p>暂无提现配置</p>
            <p class="wc-empty-sub">先添加一条收款配置，提现时即可一键选用</p>
        </div>

        <div class="wc-add-btn">
            <van-button round type="info" size="large" color="linear-gradient(to right, #FF704D, #F73727)" @click="openEditor(null)">
                + 新增配置
            </van-button>
        </div>

        <!-- 新增/编辑 弹层 -->
        <van-popup v-model="editor.show" position="bottom" round :style="{ height: '85%' }">
            <div class="wc-editor">
                <div class="wc-editor-title">{{ editor.id ? '编辑配置' : '新增配置' }}</div>
                <div class="wc-editor-form">
                    <!-- 提现方式(新增可选，编辑锁定) -->
                    <van-field
                        readonly clickable name="picker"
                        :value="payTypeText" label=""
                        placeholder="选择提现方式"
                        right-icon="arrow-down"
                        @click="!editor.id && openPicker('pay_type', payTypeOptions)"
                        :disabled="!!editor.id"
                    />
                    <template v-if="['waffo', 'onerway'].includes(editor.pay_type)">
                        <!-- 收款国家 -->
                        <van-field
                            readonly clickable name="picker"
                            :value="countryText"
                            label="收款国家"
                            placeholder="选择收款国家/地区"
                            right-icon="arrow-down"
                            required
                            @click="openPicker('payee_country', countryOptions)"
                            :disabled="!!editor.id"
                        />

                        <!-- 必填字段区域 -->
                        <div class="field-section" v-if="requiredFields.length">
                            <div class="field-section-title">
                                <span class="required-star">*</span> 必填信息（{{ requiredFields.length }} 项）
                            </div>
                            <template v-for="f in requiredFields">
                                <van-field
                                    v-if="f.type === 'picker'"
                                    :key="f.key"
                                    readonly clickable name="picker"
                                    :value="pickerTexts[f.key] || ''"
                                    :label="f.label"
                                    :placeholder="'请选择' + f.label"
                                    right-icon="arrow-down"
                                    required
                                    @click="openPicker(f.key, f.options)"
                                />
                                <van-field
                                    v-else
                                    :key="f.key"
                                    v-model="editor.form[f.key]"
                                    autocomplete="off"
                                    :label="f.label"
                                    :placeholder="f.placeholder || ('请输入' + f.label)"
                                    :maxlength="f.maxLength || 128"
                                    required
                                />
                            </template>
                        </div>

                        <!-- 选填字段区域 -->
                        <div class="field-section" v-if="optionalFields.length">
                            <div class="field-section-title optional">
                                <span class="optional-icon">○</span> 选填信息（{{ optionalFields.length }} 项，可提高成功率）
                            </div>
                            <template v-for="f in optionalFields">
                                <van-field
                                    v-if="f.type === 'picker'"
                                    :key="f.key"
                                    readonly clickable name="picker"
                                    :value="pickerTexts[f.key] || ''"
                                    :label="f.label"
                                    :placeholder="'请选择' + f.label + '（选填）'"
                                    right-icon="arrow-down"
                                    @click="openPicker(f.key, f.options)"
                                />
                                <van-field
                                    v-else
                                    :key="f.key"
                                    v-model="editor.form[f.key]"
                                    autocomplete="off"
                                    :label="f.label"
                                    :placeholder="f.placeholder || (f.label + '（选填）')"
                                    :maxlength="f.maxLength || 128"
                                />
                            </template>
                        </div>
                    </template>
                    <template v-else-if="editor.pay_type === 'paydify'">
                        <div class="field-section">
                            <div class="field-section-title">
                                <span class="required-star">*</span> 必填信息
                            </div>
                            <van-field
                                readonly clickable name="picker"
                                :value="pickerTexts['currency'] || ''"
                                label="币种"
                                placeholder="请选择币种"
                                right-icon="arrow-down"
                                required
                                @click="openPicker('currency', currencyOptions)"
                            />
                            <van-field
                                readonly clickable name="picker"
                                :value="pickerTexts['network'] || ''"
                                label="链网络"
                                placeholder="请选择链网络"
                                right-icon="arrow-down"
                                required
                                @click="openPicker('network', networkOptions)"
                            />
                            <van-field
                                v-model="editor.form['wallet_address']"
                                autocomplete="off"
                                label="钱包地址"
                                placeholder="请输入钱包地址"
                                required
                            />
                        </div>
                    </template>
                    <van-field
                        v-model="editor.form.label"
                        autocomplete="off"
                        label="备注名"
                        placeholder="备注名(选填)，如：我的BDO卡"
                    />
                </div>
                <div class="wc-editor-btn">
                    <van-button round type="info" size="large" color="linear-gradient(to right, #FF704D, #F73727)"
                        :loading="saving" loading-text="保存中，请稍候…" @click="saveConfig">
                        保存
                    </van-button>
                </div>
            </div>
        </van-popup>

        <!-- 通用选择器 -->
        <van-popup v-model="picker.show" position="bottom">
            <van-picker
                show-toolbar
                :columns="picker.columns"
                @confirm="onPickerConfirm"
                @cancel="picker.show = false"
            />
        </van-popup>
    </div>
</template>
<script>
import Top from '@/components/top'

export default {
    name: 'WithdrawConfig',
    components: {
        Top,
    },
    data() {
        return {
            list: [],
            // 后端下发的渠道/国家字段 schema(单一数据源)
            schema: { channels: {} },
            // Onerway 配置保存需串行调用两次渠道接口(必填字段查询+添加受益人)，用 loading 防重复提交
            saving: false,
            editor: {
                show: false,
                id: 0,
                pay_type: '',
                form: {},
            },
            pickerTexts: {},
            picker: {
                show: false,
                columns: [],
                key: '',
            },
        }
    },
    computed: {
        payTypeOptions() {
            return [
                { text: '法币银行卡(Waffo)', id: 'waffo' },
                { text: '法币银行卡(Onerway)', id: 'onerway' },
                { text: 'USDT/USDC 链上提现', id: 'paydify' },
            ];
        },
        payTypeText() {
            const hit = this.payTypeOptions.find(o => o.id === this.editor.pay_type);
            return hit ? hit.text : '';
        },
        countryOptions() {
            // 根据选择的渠道获取对应的国家配置
            const channelKey = this.editor.pay_type;
            if (!channelKey || !['waffo', 'onerway'].includes(channelKey)) {
                return [];
            }
            const channel = this.schema.channels[channelKey] || {};
            return (channel.countries || []).map(c => ({ text: c.text + '（' + c.currency + '）', id: c.id }));
        },
        countryText() {
            const hit = this.countryOptions.find(o => o.id === this.editor.form.payee_country);
            return hit ? hit.text : '';
        },
        // 当前国家的字段定义(前端渲染与后端校验同一数据源)。
        countryFields() {
            // 根据选择的渠道获取对应的字段配置
            const channelKey = this.editor.pay_type;
            if (!channelKey || !['waffo', 'onerway'].includes(channelKey)) {
                return [];
            }
            const channel = this.schema.channels[channelKey] || {};
            const country = (channel.countries || []).find(c => c.id === this.editor.form.payee_country);
            return ((country && country.fields) || []);
        },
        // 必填字段
        requiredFields() {
            return this.countryFields.filter(f => f.required);
        },
        // 选填字段
        optionalFields() {
            return this.countryFields.filter(f => !f.required);
        },
        paydifyFields() {
            const paydify = this.schema.channels.paydify || {};
            return paydify.fields || [];
        },
        currencyOptions() {
            const f = this.paydifyFields.find(f => f.key === 'currency');
            return (f && f.options) || [];
        },
        networkOptions() {
            const f = this.paydifyFields.find(f => f.key === 'network');
            const by = (f && f.optionsBy) || {};
            return by[this.editor.form.currency] || [];
        },
    },
    created() {
        this.loadSchema();
        this.loadList();
    },
    methods: {
        loadSchema() {
            this.$api.account.getWithdrawSchema().then(res => {
                if (res.code == 200) {
                    this.schema = res.data || { channels: {} };
                }
            }).catch(err => {
                console.log(err);
            });
        },
        loadList() {
            this.$api.account.getWithdrawConfigList().then(res => {
                if (res.code == 200) {
                    this.list = res.data.list || [];
                }
            }).catch(err => {
                console.log(err);
            });
        },
        defaultLabel(item) {
            return ['waffo', 'onerway'].includes(item.pay_type)
                ? (item.country_name + '银行卡')
                : (item.currency + '钱包');
        },
        mask(no) {
            no = String(no || '');
            if (no.length <= 8) return no;
            return no.substring(0, 4) + '****' + no.substring(no.length - 4);
        },
        openEditor(item) {
            this.pickerTexts = {};
            if (item) {
                // 编辑：字段从配置行回填(补充字段在 extra 里，key 映射回平铺表单)
                const extra = item.extra || {};
                this.editor = {
                    show: true,
                    id: item.id,
                    pay_type: item.pay_type,
                    form: {
                        payee_country: item.payee_country || '',
                        payout_account_no: item.payout_account_no || '',
                        payee_name: item.payee_name || '',
                        payee_id_type: item.payee_id_type || '',
                        payee_id_number: item.payee_id_number || '',
                        payee_phone: item.payee_phone || '',
                        bank_code: item.bank_code || '',
                        bank_name: item.bank_name || '',
                        bank_branch: extra.bankBranch || '',
                        account_type: extra.accountType || '',
                        address: extra.address || '',
                        city: extra.city || '',
                        region: extra.region || '',
                        postcode: extra.postcode || '',
                        swift_code: extra.swiftCode || '',
                        bank_address: extra.bankAddress || '',
                        payee_email: extra.email || '',
                        wallet_address: item.wallet_address || item.payout_account_no || '',
                        network: item.network || '',
                        currency: item.currency || '',
                        label: item.label || '',
                    },
                };
                this.syncPickerTexts();
            } else {
                this.editor = {
                    show: true,
                    id: 0,
                    pay_type: '',
                    form: { label: '' },
                };
            }
        },
        // 按 schema/配置回填 picker 显示文本
        syncPickerTexts() {
            const setText = (key, options) => {
                const hit = (options || []).find(o => o.id === this.editor.form[key]);
                if (hit) this.$set(this.pickerTexts, key, hit.text);
            };
            setText('account_type', [
                { text: '支票账户 CHECKING', id: 'CHECKING' },
                { text: '储蓄账户 SAVINGS', id: 'SAVINGS' },
            ]);
            setText('payee_id_type', [
                { text: '身份证', id: 'ID' },
                { text: '护照', id: 'PASSPORT' },
            ]);
            setText('currency', this.currencyOptions);
            setText('network', this.networkOptions);
        },
        openPicker(key, options) {
            if (!options || !options.length) {
                this.$toast({ message: '请先完成前置选项', duration: 1500 });
                return;
            }
            this.picker = { show: true, columns: options, key: key };
        },
        onPickerConfirm(val) {
            const key = this.picker.key;
            if (!key) return;
            this.$set(this.editor.form, key, val.id);
            this.$set(this.pickerTexts, key, val.text);
            this.picker.show = false;

            if (key === 'pay_type') {
                // 渠道存 editor.pay_type(供 payTypeText/子表单 v-if 读取)；
                // 表单整体替换，并为该渠道预置全部字段键，保证后续 v-model 响应式
                this.editor.pay_type = val.id;
                this.pickerTexts = {};
                const form = { label: this.editor.form.label || '' };
                if (val.id === 'paydify') {
                    form.currency = '';
                    form.network = '';
                    form.wallet_address = '';
                }
                this.editor.form = form;
            }
            if (key === 'payee_country') {
                // 切换国家：按 schema 预置该国全部字段键(Vue2 需预先声明才具响应式)
                const label = this.editor.form.label || '';
                const channelKey = this.editor.pay_type;
                const channel = this.schema.channels[channelKey] || {};
                const country = (channel.countries || []).find(c => c.id === val.id);
                const form = { label: label };
                ((country && country.fields) || []).forEach(f => {
                    form[f.key] = '';
                });
                form.payee_country = val.id;
                this.editor.form = form;
                this.pickerTexts = {};
            }
            if (key === 'currency') {
                // 切换币种联动网络选项，清空已选网络
                this.editor.form.network = '';
                this.$set(this.pickerTexts, 'network', '');
            }
        },
        // 客户端按 schema 预校验(后端 WithdrawConfigLogic 会再校验一遍)
        validateForm() {
            if (!this.editor.pay_type) {
                this.$toast({ message: '请选择提现方式', duration: 1500 });
                return false;
            }
            let fields = [];
            if (['waffo', 'onerway'].includes(this.editor.pay_type)) {
                if (!this.editor.form.payee_country) {
                    this.$toast({ message: '请选择收款国家/地区', duration: 1500 });
                    return false;
                }
                // 只校验必填字段
                fields = this.requiredFields;
            } else {
                fields = this.paydifyFields.filter(f => f.key !== 'network');
                // 网络选项依赖币种，单独校验
                if (!this.editor.form.network) {
                    this.$toast({ message: '请选择链网络', duration: 1500 });
                    return false;
                }
            }
            for (const f of fields) {
                const v = (this.editor.form[f.key] || '').trim();
                if (f.required && v === '') {
                    this.$toast({ message: '请填写' + f.label, duration: 1500 });
                    return false;
                }
                if (v !== '' && f.pattern) {
                    const re = new RegExp(String(f.pattern).replace(/^\/|\/$/g, ''));
                    if (!re.test(v)) {
                        this.$toast({ message: f.patternMsg || (f.label + '格式错误'), duration: 1500 });
                        return false;
                    }
                }
            }
            return true;
        },
        saveConfig() {
            if (this.saving) return;
            if (!this.validateForm()) return;

            const params = Object.assign({}, this.editor.form, {
                id: this.editor.id || undefined,
                pay_type: this.editor.pay_type,
            });

            this.saving = true;
            const req = this.editor.id
                ? this.$api.account.updateWithdrawConfig(params)
                : this.$api.account.addWithdrawConfig(params);

            req.then(res => {
                if (res.code == 200) {
                    this.$toast({ message: '保存成功', duration: 1500 });
                    this.editor.show = false;
                    this.loadList();
                }
            }).catch(err => {
                console.log(err);
            }).finally(() => {
                this.saving = false;
            });
        },
        delConfig(item) {
            this.$dialog.confirm({
                title: '删除配置',
                message: '确定删除「' + (item.label || this.defaultLabel(item)) + '」吗？',
            }).then(() => {
                this.$api.account.deleteWithdrawConfig({ id: item.id }).then(res => {
                    if (res.code == 200) {
                        this.$toast({ message: '已删除', duration: 1500 });
                        this.loadList();
                    }
                }).catch(err => {
                    console.log(err);
                });
            }).catch(() => {});
        },
    },
}
</script>
<style scoped>
.wc-page{
    min-height: 100vh;
    background: #ececec;
    padding-bottom: 2rem;
}
.wc-tip{
    padding: .625rem 1.1875rem;
    font-size: .71875rem;
    color: #909090;
    text-align: left;
    background: #ececec;
}
.wc-list{
    margin: 0 .8125rem;
}
.wc-card{
    background: #fff;
    border-radius: 5px;
    padding: .75rem .9375rem;
    margin-bottom: .75rem;
    text-align: left;
}
.wc-card-head{
    display: flex;
    align-items: center;
    padding-bottom: .5rem;
    border-bottom: 1px dashed #e5e5e5;
}
.wc-tag{
    background: #ff4e00;
    color: #fff;
    font-size: .65rem;
    border-radius: 3px;
    padding: .1rem .4rem;
    margin-right: .5rem;
    white-space: nowrap;
}
.wc-tag.paydify{
    background: #227eeb;
}
.wc-label{
    font-size: .8125rem;
    font-weight: bold;
    color: #313131;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.wc-card-body{
    padding: .5rem 0;
}
.wc-card-body p{
    font-size: .75rem;
    line-height: 1.7em;
    color: #313131;
}
.wc-card-body p span{
    color: #909090;
    display: inline-block;
    width: 4.6em;
}
.wc-card-actions{
    display: flex;
    justify-content: flex-end;
    gap: .5rem;
    padding-top: .4rem;
    border-top: 1px dashed #e5e5e5;
}
.wc-empty{
    padding: 3rem 1rem;
    text-align: center;
    color: #909090;
    font-size: .8125rem;
}
.wc-empty-sub{
    font-size: .71875rem;
    margin-top: .4rem;
}
.wc-add-btn{
    margin: 1.5rem 10% 0;
}
.wc-editor{
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #ececec;
}
.wc-editor-title{
    padding: .875rem 0;
    font-size: .9375rem;
    font-weight: bold;
    color: #313131;
    background: #fff;
}
.wc-editor-form{
    flex: 1;
    overflow-y: auto;
    padding: .8125rem 1rem;
}
.wc-editor-form .van-cell{
    background-color: #efefef;
    margin-bottom: .78125rem;
    border-radius: 5px;
}
/* 字段分区样式 */
.field-section{
    margin-bottom: .9375rem;
}
.field-section-title{
    padding: .625rem .9375rem .3125rem;
    font-size: .8125rem;
    font-weight: bold;
    color: #ff4e00;
    display: flex;
    align-items: center;
    background: #fff7f4;
    border-radius: 5px;
    margin-bottom: .625rem;
}
.field-section-title.optional{
    color: #227eeb;
    background: #f0f7ff;
}
.required-star{
    color: #ff4e00;
    font-size: 1rem;
    margin-right: .3rem;
    font-weight: bold;
}
.optional-icon{
    color: #227eeb;
    font-size: .875rem;
    margin-right: .3rem;
    font-weight: normal;
}
.field-section .van-cell{
    background-color: #fff;
    margin-bottom: .5rem;
    border-radius: 5px;
    border: 1px solid #e5e5e5;
}
.field-section .van-cell--required::before{
    color: #ff4e00;
}
.wc-editor-btn{
    padding: .75rem 10%;
    background: #fff;
}
</style>
<style>
.wc-editor-form .van-field__control{
    text-align: left;
    font-size: .8125rem;
}
</style>
